import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, HostListener, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  ChannelService,
  ConfirmationComponent,
  CountryModel,
  UtilHelper,
  WA_ORDER_DETAILS_HEADER_FORMATS,
  WA_TEMPLATE_LANGUAGES,
  WA_TEMPLATE_LIMITS,
  WA_TEMPLATE_MEDIA_ACCEPT,
  WaTemplateButtonType,
  WaTemplateLanguage,
  WaTemplateParameterFormat,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { TemplateFormEngine, TemplateHeaderFormatOption } from './template-form-engine';

/** WhatsApp's own text markers (RF-20). Monospace is the only one that is not a single character. */
type TextStyle = 'BOLD' | 'ITALIC' | 'STRIKETHROUGH' | 'MONOSPACE';

const TEXT_MARKERS: { [style: string]: { open: string, close: string } } = {
  BOLD: { open: '*', close: '*' },
  ITALIC: { open: '_', close: '_' },
  STRIKETHROUGH: { open: '~', close: '~' },
  MONOSPACE: { open: '```', close: '```' },
};

/**
 * The template fields: identity, variables, header, body, footer, buttons and samples.
 *
 * It owns no state. Every control belongs to the host's FormGroup and every operation on it goes
 * through the shared `TemplateFormEngine`, so what the operator sees here and what is saved from the
 * host can never diverge. It is a separate component only to keep the page component thin.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder.
 */
@Component({
    selector: 'template-fields',
    templateUrl: './template-fields.component.html',
    styleUrls: ['./template-fields.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemplateFieldsComponent extends AbstractComponent implements OnInit {

  /** Declared by AbstractComponent; taken as an input here so the host stays the single owner. */
  @Input() override formGroup: FormGroup;
  @Input() engine: TemplateFormEngine;
  /**
   * The channel id from the route, not the loaded Channel: the media upload must not depend on an
   * object that arrives asynchronously, or attaching a file early builds `/templates/undefined/media`.
   */
  @Input() idChannel: string;
  /** Edit mode locks identity (RF-32) — the fields are shown, not editable. */
  @Input() editing: boolean = false;

  readonly limits = WA_TEMPLATE_LIMITS;
  readonly languages: ReadonlyArray<WaTemplateLanguage> = WA_TEMPLATE_LANGUAGES;
  readonly countries: Array<CountryModel> = UtilHelper.getCountries();

  private static readonly HEADER_FORMATS: ReadonlyArray<{ value: TemplateHeaderFormatOption, label: string }> = [
    { value: 'NONE', label: 'channel.templateBuilder.mediaNone' },
    { value: 'IMAGE', label: 'channel.templateBuilder.mediaImage' },
    { value: 'VIDEO', label: 'channel.templateBuilder.mediaVideo' },
    { value: 'DOCUMENT', label: 'channel.templateBuilder.mediaDocument' },
    { value: 'LOCATION', label: 'channel.templateBuilder.mediaLocation' },
  ];

  /** Narrowed in Pix mode — the order details format accepts TEXT, IMAGE and DOCUMENT only. */
  headerFormats: ReadonlyArray<{ value: TemplateHeaderFormatOption, label: string }> = TemplateFieldsComponent.HEADER_FORMATS;

  readonly parameterFormats: Array<{ value: WaTemplateParameterFormat, label: string }> = [
    { value: 'POSITIONAL', label: 'channel.templateBuilder.variableTypeNumber' },
    { value: 'NAMED', label: 'channel.templateBuilder.variableTypeName' },
  ];

  /** RF-35 — exactly these five, in the order of the reference screen. */
  readonly buttonTypes: Array<{ value: WaTemplateButtonType, label: string }> = [
    { value: 'QUICK_REPLY', label: 'channel.templateBuilder.buttonCustom' },
    { value: 'URL', label: 'channel.templateBuilder.buttonUrl' },
    { value: 'VOICE_CALL', label: 'channel.templateBuilder.buttonVoiceCall' },
    { value: 'PHONE_NUMBER', label: 'channel.templateBuilder.buttonPhoneNumber' },
    { value: 'COPY_CODE', label: 'channel.templateBuilder.buttonCopyCode' },
  ];

  readonly urlTypes: Array<{ value: string, label: string }> = [
    { value: 'STATIC', label: 'channel.templateBuilder.urlTypeStatic' },
    { value: 'DYNAMIC', label: 'channel.templateBuilder.urlTypeDynamic' },
  ];

  /** RF-38 — the two Custom sub-types. */
  readonly customSubTypes: Array<{ value: string, label: string }> = [
    { value: 'CUSTOM', label: 'channel.templateBuilder.subTypeCustom' },
    { value: 'PRECONFIGURED_REPLY', label: 'channel.templateBuilder.subTypePreconfiguredReply' },
  ];

  /** Documentation Meta points to for enabling calls (RF-42). */
  readonly voiceCallDocumentationUrl = 'https://developers.facebook.com/docs/whatsapp/cloud-api/calling';

  emojiTranslator: unknown;
  showEmojiPicker: boolean = false;
  uploadingMedia: boolean = false;

  private bodyCaret: number = 0;
  private headerCaret: number = 0;

  constructor(
    injector: Injector,
    private readonly channelService: ChannelService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.emojiTranslator = UtilHelper.getEmojiTranslator();
    TemplateFormEngine.callingCodes = this.countries.map((country) => country.code);

    // 'NONE' is not a provider format — it stands for "no header component", which every format
    // allows. Computed once here rather than read as a getter, so it does not run on every
    // change-detection pass of a form this size.
    if (this.engine?.pixMode) {
      this.headerFormats = TemplateFieldsComponent.HEADER_FORMATS
        .filter((format) => format.value === 'NONE' || WA_ORDER_DETAILS_HEADER_FORMATS.indexOf(format.value as never) !== -1);
    }
  }

  // ----------------------------------------------------------------- identity

  get nameControl(): AbstractControl {
    return this.formGroup?.get('name');
  }

  get nameLength(): number {
    return (this.nameControl?.value ?? '').length;
  }

  /**
   * RF-09 — a rejected name needs a message naming the allowed characters, which is why this does
   * not go through `field-error-component`: its pattern message only says the pattern is invalid.
   */
  get nameRejected(): boolean {
    const control = this.nameControl;
    return !!control && control.hasError('pattern') && (control.dirty || control.touched);
  }

  length(control: string): number {
    return (this.formGroup?.get(control)?.value ?? '').length;
  }

  /**
   * A required control the operator has already been to, and left empty. Shown inline because the
   * save button disables itself on `formGroup.invalid` — without this the button just greys out with
   * nothing on screen saying which field is missing.
   *
   * `field-error-component` is not used here: its thirteen messages are hardcoded pt-BR with no
   * translate pipe, so adopting it would put untranslated text in a translated screen.
   */
  isMissing(control: string): boolean {
    return this.isMissingIn(this.formGroup, control);
  }

  isMissingIn(group: FormGroup, control: string): boolean {
    const field = group?.get(control);
    return !!field && field.hasError('required') && (field.dirty || field.touched);
  }

  // ---------------------------------------------------------------- variables

  /**
   * RF-14 — changing the variable type rewrites every marker already in the text, so it is only
   * done after the operator confirms. The select is reverted when they decline.
   */
  onParameterFormatChange(target: WaTemplateParameterFormat): void {
    if (!this.engine.hasParameters) {
      this.engine.rewriteParameters(target);
      return;
    }

    // Only two formats exist, so the one being left is the other one.
    const previous: WaTemplateParameterFormat = target === 'NAMED' ? 'POSITIONAL' : 'NAMED';
    this.dialog
      .open(ConfirmationComponent, { width: '500px', data: this.t('channel.templateBuilder.variableTypeConfirmation') })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.engine.rewriteParameters(target);
        } else {
          this.formGroup.get('parameterFormat').setValue(previous, { emitEvent: false });
        }
      });
  }

  rememberCaret(field: 'body' | 'headerText', element: HTMLTextAreaElement | HTMLInputElement): void {
    if (field === 'body') {
      this.bodyCaret = element.selectionStart ?? 0;
    } else {
      this.headerCaret = element.selectionStart ?? 0;
    }
  }

  insertBodyParameter(element: HTMLTextAreaElement): void {
    const caret = this.engine.insertParameter(this.formGroup.get('body'), this.bodyCaret, this.engine.bodyParameters);
    this.restoreCaret(element, caret);
  }

  insertHeaderParameter(element: HTMLInputElement): void {
    const caret = this.engine.insertParameter(this.formGroup.get('headerText'), this.headerCaret, this.engine.headerParameters);
    this.restoreCaret(element, caret);
  }

  /** RF-13 — for a named marker the caret must land inside `{{}}` so the name can be typed. */
  private restoreCaret(element: HTMLTextAreaElement | HTMLInputElement, caret: number): void {
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(caret, caret);
    });
  }

  // --------------------------------------------------------------------- body

  /** RF-20 — wraps the selection, or opens an empty pair at the caret when nothing is selected. */
  applyStyle(element: HTMLTextAreaElement, style: TextStyle): void {
    const marker = TEXT_MARKERS[style];
    const text: string = this.formGroup.get('body').value ?? '';
    const start = element.selectionStart ?? text.length;
    const end = element.selectionEnd ?? start;
    const selected = text.slice(start, end);

    this.formGroup.get('body').setValue(
      `${text.slice(0, start)}${marker.open}${selected}${marker.close}${text.slice(end)}`,
    );
    this.formGroup.get('body').markAsDirty();
    this.restoreCaret(element, start + marker.open.length + selected.length);
  }

  /**
   * The click is stopped here so the document listener below does not immediately close what this
   * very click just opened.
   */
  toggleEmojiPicker(event: Event): void {
    event.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  /** Clicking anywhere outside the picker closes it, as the other emoji panels in the app do. */
  @HostListener('document:click')
  closeEmojiPicker(): void {
    this.showEmojiPicker = false;
  }

  addEmoji(event: { emoji?: { native?: string } }, element: HTMLTextAreaElement): void {
    const emoji = event?.emoji?.native ?? '';
    const text: string = this.formGroup.get('body').value ?? '';
    const caret = Math.min(this.bodyCaret, text.length);

    this.formGroup.get('body').setValue(`${text.slice(0, caret)}${emoji}${text.slice(caret)}`);
    this.formGroup.get('body').markAsDirty();
    this.showEmojiPicker = false;
    this.restoreCaret(element, caret + emoji.length);
  }

  // ------------------------------------------------------------------- header

  get mediaAccept(): string {
    return WA_TEMPLATE_MEDIA_ACCEPT[this.formGroup.get('headerFormat').value] ?? '';
  }

  dragging: boolean = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
  }

  /** Real drop handling — the other upload screens promise it in the copy and never implement it. */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;

    const file = event.dataTransfer?.files?.[0];
    if (!!file && !this.uploadingMedia) {
      this.uploadMedia(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    // Cleared so picking the same file again after a failure still fires a change event.
    input.value = '';
    this.uploadMedia(file);
  }

  /** RNF-04 — the file goes up as soon as it is attached, so a rejection is immediate. */
  uploadMedia(file: File): void {
    this.uploadingMedia = true;
    this.channelService.uploadTemplateMediaByChannel(this.idChannel, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.formGroup.get('headerHandle').setValue(result?.handle);
          this.formGroup.get('headerHandle').markAsDirty();
          this.engine.mediaFileName = file.name;
          this.engine.currentMediaUrl = null;
          this.uploadingMedia = false;
        },
        error: (err) => {
          this.uploadingMedia = false;
          this.alertService.error(err?.error?.message ?? this.t('channel.templateBuilder.mediaUploadFailed', { formats: this.mediaAccept }));
        },
      });
  }

  removeMedia(): void {
    this.formGroup.get('headerHandle').setValue(null);
    this.engine.mediaFileName = null;
    this.engine.currentMediaUrl = null;
  }

  // ------------------------------------------------------------------ buttons

  get buttons(): FormArray {
    return this.engine.buttons;
  }

  buttonGroup(index: number): FormGroup {
    return this.buttons.at(index) as FormGroup;
  }


  get callToActionIndexes(): Array<number> {
    return this.engine.familyIndexes('CALL_TO_ACTION');
  }

  get quickReplyIndexes(): Array<number> {
    return this.engine.familyIndexes('QUICK_REPLY');
  }

  get canAddAnyButton(): boolean {
    // A Pix template carries its single ORDER_DETAILS button and no other — nothing to add.
    return !this.engine.pixMode && this.buttons.length < this.limits.BUTTONS_MAX;
  }

  addButton(type: WaTemplateButtonType): void {
    this.engine.addButton(type);
    this.buttons.markAsDirty();
  }

  removeButton(index: number): void {
    this.engine.removeButton(index);
  }


  /** RF-46 — the drop indexes are positions inside one family, mapped back to the whole array. */
  dropButton(event: CdkDragDrop<Array<number>>, indexes: Array<number>): void {
    this.engine.moveButton(indexes[event.previousIndex], indexes[event.currentIndex]);
  }



  // ------------------------------------------------------------------ samples

  get headerExamples(): FormArray {
    return this.engine.headerExamples;
  }

  get bodyExamples(): FormArray {
    return this.engine.bodyExamples;
  }

  get hasSamples(): boolean {
    return this.engine.headerParameters.length > 0
      || this.engine.bodyParameters.length > 0
      || this.engine.dynamicUrlButtonIndexes.length > 0;
  }

  /**
   * Renders a parameter the way it appears in the text. The samples are now tracked by control, so
   * a change-detection pass can land between the array being rebuilt and the parameter list being
   * reassigned — one tick with no name for that row, which must not print `{{undefined}}`.
   */
  parameterLabel(parameter: string): string {
    return !parameter ? '' : `{{${parameter}}}`;
  }
}
